import { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import DetailsCard from "@/components/DetailsCard";
import EmptyState from "@/components/EmptyState";
import SideSheetModal from "@/components/SideSheetModal";
import Tag, { type TagType } from "@/components/Tag/Tag";
import usePagination from "@/hooks/usePagination";
import { useCanManageGrpCommissionBool } from "@/hooks/useCapabilities";
import usePageTitle from "@/hooks/usePageTitle";
import { toFormat } from "@/lib/dateHelpers";
import { useNavigate } from "@/router";
import CommissionTabLayout from "../_CommissionTabLayout";
import { formatEnumLabel } from "../_utils";
import { useCanShowCommissionPlatformBool, useRequireCommissionPlatform } from "../useCommissionPlatformGate";
import { COMMISSION_CAMPAIGNS_QUERY } from "./queries";
import { getColumns } from "./_components/TableColumns";
import type { CommissionCampaignNode } from "./types";

function campaignStatusTagType(status: CommissionCampaignNode["status"]): TagType {
  if (status === "ACTIVE") return "success";
  if (status === "DRAFT") return "warn";
  return "surface";
}

export default function CommissionCampaignsPage() {
  usePageTitle("/billing/finance/commission/campaigns", "Campaigns");
  useRequireCommissionPlatform();
  const canQuery = useCanShowCommissionPlatformBool();
  const [selectedCampaign, setSelectedCampaign] = useState<CommissionCampaignNode | null>(null);
  const navigate = useNavigate();
  const canManageCommission = useCanManageGrpCommissionBool();
  const { paginationVariables, createHeaderPagination, createFooterPagination } = usePagination();
  const columns = useMemo(() => getColumns(), []);
  const tableActions = useMemo(
    () =>
      canManageCommission
        ? [
            {
              label: "Add campaign",
              icon: "add" as const,
              onClick: () => navigate("/billing/finance/commission/campaigns/new")
            }
          ]
        : [],
    [canManageCommission, navigate]
  );
  const { data, previousData, loading, error } = useQuery(COMMISSION_CAMPAIGNS_QUERY, {
    variables: paginationVariables,
    skip: !canQuery
  });

  const connection = data?.commissionCampaigns ?? previousData?.commissionCampaigns;
  const items = connection?.nodes ?? [];
  const totalRowCount = connection?.totalCount ?? items.length;

  if (error) {
    return (
      <CommissionTabLayout>
        <EmptyState title="Error" caption="An error occurred while loading campaigns." iconName="error" />
      </CommissionTabLayout>
    );
  }

  return (
    <CommissionTabLayout>
      <TableHeading body="Annual campaign editions tied to sponsor partners and programs." actions={tableActions} />
      <Table
        data={items}
        columns={columns}
        loading={loading}
        onClickRow={(row) => setSelectedCampaign(row.original)}
        renderNoDataEmptyState={() => (
          <EmptyState title="No Campaigns Yet." caption="Campaigns will appear here once configured." iconName="list" />
        )}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            pagination={createHeaderPagination(connection, {
              loading,
              pageCount: items.length,
              totalCount: totalRowCount
            })}
            onSortClick={onSortClick}
          />
        )}
        renderFooter={() => (
          <TableFooter
            pagination={createFooterPagination(connection, {
              loading,
              pageCount: items.length,
              totalCount: totalRowCount
            })}
            totalRowCount={totalRowCount}
          />
        )}
      />
      <SideSheetModal
        isSidebarOpen={!!selectedCampaign}
        setSidebarOpen={(open) => {
          if (!open) setSelectedCampaign(null);
        }}
        header={{
          title: selectedCampaign ? `${selectedCampaign.editionYear} campaign` : "Campaign details"
        }}
        disableFocusManagement
      >
        {selectedCampaign && (
          <DetailsCard
            compact
            heading="Details"
            options={[
              {
                overline: "Edition year",
                headline: String(selectedCampaign.editionYear)
              },
              {
                overline: "Partner",
                headline: selectedCampaign.partner.name
              },
              {
                overline: "Program",
                headline: selectedCampaign.program.name,
                supportingText: selectedCampaign.program.slug
              },
              {
                overline: "Status",
                headline: formatEnumLabel(selectedCampaign.status),
                status: () => (
                  <Tag tagType={campaignStatusTagType(selectedCampaign.status)} tagStyle="strong">
                    {formatEnumLabel(selectedCampaign.status)}
                  </Tag>
                )
              },
              {
                overline: "Payout cadence",
                headline: formatEnumLabel(selectedCampaign.payoutCadence)
              },
              {
                overline: "Starts",
                headline: toFormat(selectedCampaign.startsAt) || selectedCampaign.startsAt
              },
              {
                overline: "Ends",
                headline: toFormat(selectedCampaign.endsAt) || selectedCampaign.endsAt
              }
            ]}
          />
        )}
      </SideSheetModal>
    </CommissionTabLayout>
  );
}
